import { useMemo } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { CheckIcon, EditOutlineIcon, LinkIcon, ShareNowIcon, VisibilityIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { ActionButtonProps } from '../../../../types';

import { actionButton as actionButtonStyles } from './ActionButton.styles';
import { useActionButtons } from './hooks/useActionButtons';

type ButtonCustomProps = {
	icon: JSX.Element;
	handleClick: () => void | Promise<void>;
	id: string;
	label: string;
};

export const ActionButton: React.FC<ActionButtonProps> = ({ actionType, variant = 'default' }) => {
	const {
		data: { copied },
		actions: { handleCopyLinkClick, handleEditPetitionClick, handleViewPetitionClick, handleSharePetitionClick },
	} = useActionButtons();
	const { translate } = useI18n();
	const { icon, handleClick, id, label } = useMemo((): ButtonCustomProps => {
		switch (actionType) {
			case 'edit':
				return {
					icon: <EditOutlineIcon aria-label="edit-icon" />,
					handleClick: handleEditPetitionClick,
					id: 'edit-header-link',
					label: 'edit_petition_link',
				};
			case 'link':
				return {
					icon: copied ? (
						<CheckIcon aria-label="link-copied-icon" className="text-icon-success" />
					) : (
						<LinkIcon aria-label="link-icon" data-qa="copy-link-icon" />
					),
					handleClick: handleCopyLinkClick,
					id: 'copy-link-header-button',
					label: copied ? 'copy_link_button.copied_state' : 'copy_link_button.default_state',
				};
			case 'view':
				return {
					icon: <VisibilityIcon aria-label="view-icon" />,
					handleClick: handleViewPetitionClick,
					id: 'petition-details-header-link',
					label: 'view_petition_live_link',
				};
			case 'share':
			case 'shareExperimental':
			default:
				return {
					icon: <ShareNowIcon aria-label="share-icon" />,
					handleClick: handleSharePetitionClick,
					id: 'share-link-header-button',
					label: 'share_link_button',
				};
		}
	}, [
		actionType,
		copied,
		handleCopyLinkClick,
		handleEditPetitionClick,
		handleViewPetitionClick,
		handleSharePetitionClick,
	]);

	const styles = actionButtonStyles({ variant, actionType });

	return (
		<Button
			key={actionType}
			data-qa={id}
			data-testid={id}
			leadingIcon={icon}
			onClick={handleClick}
			size="x-small"
			variant={actionType === 'share' ? 'primary' : 'secondary'}
			className={styles}
			aria-label={translate(
				`corgi.petition.starter_dashboard.header_navigation.${actionType === 'view' ? 'view_petition_live_link_a11y' : label}`,
			)}
		>
			<Translate value={`corgi.petition.starter_dashboard.header_navigation.${label}`} />
		</Button>
	);
};
